import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Components
import BaseContentContainer from '../../components/base-content-container/base-content-container';
import Form from '../../components/form/form';
import ButtonsBar from '../../components/buttons-bar/buttons-bar';
import SubmitButton from '../../components/submit-button/submit-button';
import Loading from '../../components/loading/loading';
import ErrorComponent from '../../components/error/error';

// Models
import {
  BaseContentContainerActionButtonPosition,
  BaseContentContainerBiggerSide,
  BaseContentContainerFormat,
} from '../../models/base-content-container';

// Services
import ManagementService from '../../services/management-service';

// Form Data
import { getFormDataManagementStudent } from '../../form-data/form-data-management-student';

// Providers
import { GeneralContext } from '../../providers/general-provider';

// Helpers
import { formatPhoneNumber } from '../../helpers/string-format-helper';
import { validateEmail, validatePhone } from '../../helpers/validate-helper';

const ManagementStudent = () => {
  const navigate = useNavigate();
  const { setModalContent, setToastContent } = useContext(GeneralContext);
  const managementService = new ManagementService();
  const { studentId, classroomId } = useParams<{
    studentId?: string;
    classroomId?: string;
  }>();

  const [studentFormData, setStudentFormData] = useState<FormParams>(null);
  const [responsibleFormData, setResponsibleFormData] = useState<FormParams>(
    null
  );

  const initialData: ManagementStudentData = {
    studentName: null,
    studentClassroom: null,
    studentGender: null,
    firstResponsibleName: null,
    firstResponsiblePhone: null,
    firstResponsibleEmail: null,
    secondResponsibleName: null,
    secondResponsiblePhone: null,
    secondResponsibleEmail: null,
  };
  const [data, setData] = useState<ManagementStudentData>(initialData);
  const [validations, setValidations] = useState({});
  const [allowSubmit, setAllowSubmit] = useState(false);
  useEffect(() => {
    const hasSecondResponsible =
      data.secondResponsibleName || data.secondResponsiblePhone;
    const secondResponsibleFilled =
      data.secondResponsibleName && data.secondResponsiblePhone;

    if (
      data.studentName &&
      data.studentClassroom >= 0 &&
      data.studentGender &&
      data.firstResponsibleName &&
      data.firstResponsiblePhone &&
      Object.keys(validations).length === 0
    ) {
      if (hasSecondResponsible && !secondResponsibleFilled) {
        setAllowSubmit(false);
      } else {
        setAllowSubmit(true);
      }
    } else if (allowSubmit) {
      setAllowSubmit(false);
    }
  }, [validations, allowSubmit, data]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputFormChange = (
    evt: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const id = evt.target.id;
    let value = evt.target.value;

    const newValidations = Object.assign({}, validations);

    if (id.includes('first')) {
      if (id === 'firstResponsiblePhone') {
        value = value.replace(/\D/g, '');
        const element = document.getElementById(
          'firstResponsiblePhone'
        ) as HTMLInputElement;
        element.value = value;

        const phoneValidate = validatePhone(value);

        if (!value) newValidations[id] = 'Campo obrigatório.';
        else if (value && !phoneValidate.isValid)
          newValidations[id] = phoneValidate.message;
        else {
          element.value = formatPhoneNumber(value);
          delete newValidations[id];
        }
      } else if (id === 'firstResponsibleEmail') {
        const emailValidate = validateEmail(value);
        if (value && !emailValidate.isValid)
          newValidations[id] = emailValidate.message;
        else delete newValidations[id];
      } else {
        if (!value) newValidations[id] = 'Campo obrigatório.';
        else delete newValidations[id];
      }
    } else if (id.includes('second')) {
      if (id === 'secondResponsiblePhone') {
        value = value.replace(/\D/g, '');
        const element = document.getElementById(
          'secondResponsiblePhone'
        ) as HTMLInputElement;
        element.value = value;

        const phoneValidate = validatePhone(value);

        if (
          !value &&
          (data.firstResponsibleName || data.firstResponsiblePhone)
        ) {
          if (data.secondResponsibleName || data.secondResponsibleEmail)
            newValidations[id] = 'Campo obrigatório.';
          else delete newValidations['secondResponsibleName'];
        } else if (value && !phoneValidate.isValid)
          newValidations[id] = phoneValidate.message;
        else {
          element.value = formatPhoneNumber(value);
          delete newValidations[id];
        }
      } else if (id === 'secondResponsibleEmail') {
        const emailValidate = validateEmail(value);
        if (value && !emailValidate.isValid)
          newValidations[id] = emailValidate.message;
        else delete newValidations[id];

        if (
          value &&
          (!data.secondResponsibleName || !data.secondResponsiblePhone)
        ) {
          if (!data.secondResponsibleName)
            newValidations['secondResponsibleName'] = 'Campo obrigatório.';
          if (!data.secondResponsiblePhone)
            newValidations['secondResponsiblePhone'] = 'Campo obrigatório.';
        }
      } else {
        if (!value) {
          if (data.secondResponsiblePhone || data.secondResponsibleEmail)
            newValidations[id] = 'Campo obrigatório.';
          else delete newValidations['secondResponsiblePhone'];
        } else delete newValidations[id];
      }
    } else {
      if (!value || (id === 'studentGender' && value === 'default'))
        newValidations[id] = 'Campo obrigatório.';
      else delete newValidations[id];
    }

    const newData = Object.assign({}, data);
    newData[id] = id === 'studentClassroom' ? parseInt(value) : value;

    setValidations(newValidations);
    setData(newData);
  };

  const [error, setError] = useState(false);
  useEffect(() => {
    managementService
      .getClassrooms()
      .then(async (classrooms) => {
        const classroomIdNumber = classroomId ? parseInt(classroomId) : null;

        let student: Student = null;
        if (studentId)
          student = await managementService.getStudent(parseInt(studentId));

        const formData = getFormDataManagementStudent(
          classrooms,
          classroomIdNumber,
          student
        );

        const emptyResponsible = { name: null, phone: null, email: null };
        const firstResponsible =
          student && student.responsibles.length > 0
            ? student.responsibles[0]
            : emptyResponsible;
        const secondResponsible =
          student && student.responsibles.length > 1
            ? student.responsibles[1]
            : emptyResponsible;

        const newData: ManagementStudentData = {
          studentName: student ? student.name : null,
          studentClassroom: classroomId
            ? parseInt(classroomId)
            : student
              ? student.classroomId
              : null,
          studentGender: student ? student.gender : null,
          firstResponsibleName: firstResponsible.name,
          firstResponsiblePhone: firstResponsible.phone,
          firstResponsibleEmail: firstResponsible.email,
          secondResponsibleName: secondResponsible.name,
          secondResponsiblePhone: secondResponsible.phone,
          secondResponsibleEmail: secondResponsible.email,
        };

        setData(newData);
        setStudentFormData(formData[0]);
        setResponsibleFormData(formData[1]);
      })
      .catch((err) => {
        console.log(err);
        setError(true);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const createNewStudent = () => {
    managementService
      .createNewStudent(data)
      .then(() => {
        setToastContent({
          isOpen: true,
          text: 'Aluno adicionado!',
          type: 'success',
        });
        navigate(`/gerenciar/turma/${data.studentClassroom}`);
      })
      .catch(() => {
        setToastContent({
          isOpen: true,
          text: 'Houve um erro ao adicionar o aluno.',
          type: 'error',
        });
      });
  };

  const updateStudent = () => {
    managementService
      .updateStudent(data, parseInt(studentId))
      .then(() => {
        setToastContent({
          isOpen: true,
          text: 'Aluno editado!',
          type: 'success',
        });
        navigate(`/gerenciar/turma/${data.studentClassroom}`);
      })
      .catch(() => {
        setToastContent({
          isOpen: true,
          text: 'Houve um erro ao salvar o aluno.',
          type: 'error',
        });
      });
  };

  const deleteStudent = () => {
    const deletePromise = () => {
      return managementService
        .deleteStudent(parseInt(studentId))
        .then(() => {
          setToastContent({
            isOpen: true,
            text: 'Aluno deletado!',
            type: 'success',
          });
          navigate(`/gerenciar/turma/${data.studentClassroom}`);
        })
        .catch(() => {
          setToastContent({
            isOpen: true,
            text: 'Houve um erro ao deletar o aluno.',
            type: 'error',
          });
        });
    };

    setModalContent({
      title: `Deletar aluno`,
      modalContent: (
        <p>
          Deseja realmente deletar o aluno <b>{data.studentName}</b>
        </p>
      ),
      submitButtonTitle: 'confirmar deleção',
      submitButtonType: 'primary',
      submitData: null,
      onSubmitButtonClick: deletePromise,
      isOpen: true,
    });
  };

  if (error) return <ErrorComponent type='contentFull' />;

  if (!studentFormData || !responsibleFormData)
    return <Loading type='contentFull' />;

  return (
    <BaseContentContainer
      biggerSide={BaseContentContainerBiggerSide.half}
      format={BaseContentContainerFormat.doubleWithActionButtons}
      actionButtonPosition={BaseContentContainerActionButtonPosition.bottom}
    >
      <Form
        formData={studentFormData}
        validations={validations}
        handleOnChange={handleInputFormChange}
      />
      <Form
        formData={responsibleFormData}
        validations={validations}
        handleOnChange={handleInputFormChange}
      />
      <ButtonsBar pattern='firstSeparate' verticalAlign='bottom'>
        <SubmitButton
          value='cancelar'
          variant='terciary'
          onClick={() => navigate(-1)}
        />
        {studentId && (
          <SubmitButton
            value='deletar aluno'
            variant='secondary'
            onClick={deleteStudent}
          />
        )}
        {studentId && (
          <SubmitButton
            value='salvar aluno'
            variant='primary'
            disabled={!allowSubmit}
            onClick={updateStudent}
          />
        )}
        {!studentId && (
          <SubmitButton
            value='adicionar aluno'
            variant='primary'
            disabled={!allowSubmit}
            onClick={createNewStudent}
          />
        )}
      </ButtonsBar>
    </BaseContentContainer>
  );
};

export default ManagementStudent;
